/**
 * Phase A + A.5 · Recovery Health card.
 *
 * Read-only. Hits `GET /admin/quotes/recovery/health` every 60s.
 * Renders three status states (HEALTHY / WARNING / CRITICAL) and
 * splits the operational data into three bands:
 *
 *   LIVE RECOVERY          — resolver-backed effective queue.
 *   DELIVERY & COVERAGE    — last-24h sends, failures, coverage.
 *   HISTORICAL / EXCLUDED  — raw legacy counts + per-reason breakdown
 *                            (collapsed by default — audit only).
 *
 * The raw historical backlog is NEVER surfaced as "customers need
 * recovery." Only the effective LIVE numbers drive the operational
 * story.
 */
import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const REFRESH_MS = 60_000;

const TONE = {
  HEALTHY:  "bg-emerald-50 border-emerald-800 text-emerald-900",
  WARNING:  "bg-amber-50   border-amber-800   text-amber-900",
  CRITICAL: "bg-red-50     border-red-800     text-red-900",
};
const DOT = {
  HEALTHY:  "bg-emerald-600",
  WARNING:  "bg-amber-600",
  CRITICAL: "bg-red-600",
};
const GATE_LABEL = {
  scheduler_dry_run:      "Dry-Run",
  automation_disabled:    "Automation OFF",
  configuration_error:    "Config Error",
};

function fmtMins(m) {
  if (m == null) return "never";
  if (m < 60)  return `${Math.round(m)}m ago`;
  if (m < 1440) return `${(m / 60).toFixed(1)}h ago`;
  return `${(m / 1440).toFixed(1)}d ago`;
}

export default function RecoveryHealthCard() {
  const [h, setH] = useState(null);
  const [err, setErr] = useState(null);
  const [showHistorical, setShowHistorical] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/quotes/recovery/health");
      setH(r.data);
      setErr(null);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || "Health check failed");
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);

  if (err) {
    return (
      <div className="border-2 border-red-800 bg-red-50 text-red-900 rounded-lg p-3 text-sm mb-4"
           data-testid="recovery-health-error">
        Recovery Health unavailable: {String(err)}
      </div>
    );
  }
  if (!h) return null;

  const status = h.status || "HEALTHY";
  const s = h.scheduler || {};
  const rec = h.recovery || {};            // legacy / historical
  const eff = h.recovery_effective || {};  // resolver-backed
  const cfg = h.configuration || {};
  const del = h.delivery || {};
  const imm = h.immediate_email || {};

  const gate = eff.execution_gate || null;
  const gateLabel = gate ? (GATE_LABEL[gate] || gate) : null;
  const dueNow = (eff.due_now_r1 ?? 0) + (eff.due_now_r2 ?? 0);
  const wouldNow = (eff.would_send_r1_now ?? 0) + (eff.would_send_r2_now ?? 0);
  const scheduled = (eff.scheduled_r1 ?? 0) + (eff.scheduled_r2 ?? 0);
  const excluded = eff.excluded || {};

  return (
    <div className="mb-4" data-testid="recovery-health">
      {status === "CRITICAL" && (
        <div className="border-2 border-red-800 bg-red-100 text-red-900 rounded-lg p-3 mb-3 flex items-center gap-3"
             data-testid="recovery-critical-banner">
          <span className={`inline-block w-2.5 h-2.5 rounded-full ${DOT.CRITICAL}`} />
          <div className="flex-1">
            <div className="font-bold uppercase tracking-wider text-xs">Recovery CRITICAL</div>
            <div className="text-xs mt-0.5">
              {(h.reasons || []).slice(0, 3).join(" · ") || "check details below"}
            </div>
          </div>
        </div>
      )}
      <div className={`border-2 rounded-xl p-4 ${TONE[status] || TONE.CRITICAL}`}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <span className={`inline-block w-2.5 h-2.5 rounded-full ${DOT[status] || DOT.CRITICAL}`} />
            <span className="font-bold uppercase tracking-widest text-xs"
                  data-testid="recovery-health-status">
              Recovery {status}
            </span>
            {gate && (
              <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded bg-white/60 border border-current"
                    data-testid="recovery-execution-gate">
                Blocked by: {gateLabel}
              </span>
            )}
          </div>
          <button
            onClick={load}
            className="text-[10px] uppercase tracking-widest font-bold underline"
            data-testid="recovery-health-refresh"
          >
            Refresh
          </button>
        </div>

        {/* ── LIVE RECOVERY (resolver-backed) ─────────────────── */}
        <SectionHeader label="Live Recovery" testid="rh-section-live" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <Cell label="Last scheduler tick"
                value={fmtMins(s.minutes_since_last_tick)}
                sub={s.last_tick_outcome || "—"} testid="rh-last-tick" />
          <Cell label="Last successful send"
                value={fmtMins(rec.last_successful_recovery_send
                  ? (Date.now() - new Date(rec.last_successful_recovery_send).getTime())/60000
                  : null)}
                sub={rec.last_successful_recovery_send?.slice(0, 10) || "never"}
                testid="rh-last-send" />
          <Cell label={gate ? "Would send now" : "Eligible now"}
                value={gate ? wouldNow : dueNow}
                sub={gate
                  ? `R1 ${eff.would_send_r1_now ?? 0} / R2 ${eff.would_send_r2_now ?? 0}`
                  : `R1 ${eff.due_now_r1 ?? 0} / R2 ${eff.due_now_r2 ?? 0}`}
                testid="rh-effective-due" />
          <Cell label="Scheduled"
                value={scheduled}
                sub={`R1 ${eff.scheduled_r1 ?? 0} / R2 ${eff.scheduled_r2 ?? 0}`}
                testid="rh-effective-scheduled" />
          <Cell label="Oldest effective overdue"
                value={fmtMins(eff.oldest_effective_overdue_minutes)}
                sub={eff.truncated ? "TRUNCATED" : `evaluated ${eff.evaluated_count ?? 0}`}
                testid="rh-effective-oldest" />
          <Cell label="Dry run"
                value={cfg.dry_run ? "ON" : "off"}
                sub={cfg.resend_configured ? "resend ok" : "no resend"}
                testid="rh-dry-run" />
          <Cell label="Boundary"
                value={cfg.automation_boundary_at ? "set" : "MISSING"}
                sub={cfg.automation_boundary_at?.slice(0, 10) || "—"}
                testid="rh-boundary" />
          <Cell label="Execution gate"
                value={gate ? gateLabel : "none"}
                sub={gate ? "sends intentionally blocked" : "sends flowing"}
                testid="rh-gate" />
        </div>

        {/* ── DELIVERY & COVERAGE ──────────────────────────── */}
        <SectionHeader label="Delivery & Coverage" testid="rh-section-delivery" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <Cell label="Failed sends 24h" value={del.failed_24h ?? 0}
                sub={`not_cfg ${del.not_configured_24h ?? 0}`} testid="rh-failed" />
          <Cell label="Successful sends 24h" value={del.successful_sends_24h ?? 0}
                sub="R1/R2" testid="rh-sends-24h" />
          <Cell label="Immediate coverage" value={`${imm.coverage_pct ?? 0}%`}
                sub={`${imm.immediate_sent_24h ?? 0}/${imm.quotes_with_email_24h ?? 0}`}
                testid="rh-coverage" />
        </div>

        {/* ── HISTORICAL / EXCLUDED (audit only, collapsed) ── */}
        <button
          onClick={() => setShowHistorical(v => !v)}
          className="mt-3 text-[10px] uppercase tracking-widest font-bold underline opacity-80"
          data-testid="rh-toggle-historical"
        >
          {showHistorical ? "▼" : "▶"} Historical / excluded ({rec.total_due ?? 0} raw)
        </button>
        {showHistorical && (
          <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs"
               data-testid="rh-historical">
            <Cell label="Raw legacy overdue" value={rec.total_due ?? 0}
                  sub={`R1 ${rec.r1_due ?? 0} / R2 ${rec.r2_due ?? 0}`}
                  testid="rh-raw-total" small />
            <Cell label="Expired (>48h)"    value={excluded.expired_past_max_age ?? 0}     testid="rh-x-expired" small />
            <Cell label="Superseded"        value={excluded.superseded_by_newer_quote ?? 0} testid="rh-x-superseded" small />
            <Cell label="Converted"         value={excluded.converted_this_quote ?? 0}      testid="rh-x-converted" small />
            <Cell label="Order in progress" value={excluded.order_in_progress_this_quote ?? 0} testid="rh-x-inprogress" small />
            <Cell label="Customer converted" value={excluded.customer_has_conversion ?? 0}  testid="rh-x-cust-conv" small />
            <Cell label="Customer cooldown" value={excluded.customer_cooldown ?? 0}         testid="rh-x-cooldown" small />
            <Cell label="Suppressed"        value={excluded.suppressed ?? 0}                testid="rh-x-suppressed" small />
            <Cell label="Before boundary"   value={excluded.historical_before_boundary ?? 0} testid="rh-x-historical" small />
            <Cell label="No email"          value={excluded.no_customer_email ?? 0}         testid="rh-x-noemail" small />
            <Cell label="Stages complete"   value={excluded.all_stages_complete ?? 0}       testid="rh-x-complete" small />
            <div className="text-[10px] opacity-70 col-span-2 md:col-span-4 mt-1">
              Raw counts are audit-only. Health status is driven by the LIVE effective queue above.
            </div>
          </div>
        )}

        {(h.reasons || []).length > 0 && status !== "HEALTHY" && (
          <div className="mt-3 text-xs opacity-80" data-testid="rh-reasons">
            <b>Reasons:</b> {(h.reasons || []).join(", ")}
          </div>
        )}
      </div>
    </div>
  );
}

function SectionHeader({ label, testid }) {
  return (
    <div className="mt-3 mb-1 text-[10px] uppercase tracking-widest font-bold opacity-70"
         data-testid={testid}>
      {label}
    </div>
  );
}

function Cell({ label, value, sub, testid, small }) {
  return (
    <div data-testid={testid}>
      <div className={`text-[10px] uppercase tracking-widest font-bold opacity-70 ${small ? "" : ""}`}>{label}</div>
      <div className={`font-display leading-tight ${small ? "text-sm" : "text-lg"}`}>{value ?? "—"}</div>
      {sub && <div className="text-[11px] opacity-70">{sub}</div>}
    </div>
  );
}
